import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useBranding } from "@/contexts/BrandingContext";

export default function PublicNavbar() {
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(!isHome);
  const [mobileOpen, setMobileOpen] = useState(false);

  // En home, al inicio el logo vive en el hero; al bajar vuelve al app bar fijo
  const showBarLogo = scrolled || !isHome;
  const navAtLeft = isHome && !scrolled;

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const id = hash.replace("#", "");
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
  };

  const hashLinks = [
    { label: "Inicio", hash: "#inicio" },
    { label: "Cursos", hash: "#cursos" },
    { label: "Diplomados", hash: "#diplomados" },
    { label: "Nosotros", hash: "#nosotros" },
    { label: "Noticias", hash: "#noticias" },
    { label: "Contacto", hash: "#contacto" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#080f1a]/95 backdrop-blur-xl border-b border-[#ffffff]/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="relative flex items-center h-[72px]">
          {/* Logo: aparece al bajar (barra fija) */}
          <div
            className={`flex items-center transition-all duration-500 ${
              showBarLogo ? "opacity-100 translate-x-0 w-auto mr-6" : "opacity-0 -translate-x-2 w-0 mr-0 overflow-hidden pointer-events-none"
            }`}
          >
            <Link to="/" className="flex items-center gap-3 group shrink-0">
              <img
                src={branding.logo_full}
                alt="KSM Services"
                className="h-10 w-auto max-w-[160px] object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.6)] brightness-0 invert"
              />
            </Link>
          </div>

          {/* Opciones: izquierda arriba; centradas cuando hay scroll */}
          <div
            className={`hidden lg:flex items-center gap-8 transition-all duration-500 ${
              navAtLeft
                ? "relative"
                : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            }`}
          >
            {hashLinks.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                onClick={(e) => handleHashClick(e, link.hash)}
                className="text-sm font-medium text-white/90 hover:text-white transition-all duration-300 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/validar-certificado"
              className="text-sm font-medium text-white/90 hover:text-white transition-all duration-300 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] whitespace-nowrap"
            >
              Validar Certificado
            </Link>
          </div>

          <div className="ml-auto hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-white/90 hover:text-white transition-all duration-300 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]"
            >
              Ingresar
            </Link>
            <Link
              to="/registro"
              className="bg-[#D4A03C] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#c2912e] transition-all shadow-lg shadow-black/30"
            >
              Registrarse
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden ml-auto w-10 h-10 flex items-center justify-center text-white"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#080f1a]/95 backdrop-blur-xl border-t border-[#ffffff]/10">
          <div className="px-4 py-6 space-y-2">
            {hashLinks.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                onClick={(e) => handleHashClick(e, link.hash)}
                className="block px-4 py-3 text-sm font-medium text-white/90 hover:text-white hover:bg-[#ffffff]/5 rounded-xl transition-all cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/validar-certificado"
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-white/90 hover:text-white hover:bg-[#ffffff]/5 rounded-xl transition-all"
            >
              Validar Certificado
            </Link>
            <div className="pt-4 flex flex-col gap-3">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="block text-center px-4 py-3 text-sm font-medium text-white/90 border border-[#ffffff]/10 rounded-xl hover:bg-[#ffffff]/5 transition-all"
              >
                Ingresar
              </Link>
              <Link
                to="/registro"
                onClick={() => setMobileOpen(false)}
                className="block text-center bg-[#D4A03C] text-white px-4 py-3 rounded-xl text-sm font-semibold hover:bg-[#c2912e] transition-all"
              >
                Registrarse
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
